import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  persistFacePending,
  persistFaceProfileSnapshot,
  readFaceCaptureBlob,
  readFaceCaptureDataUrl,
  readFacePending,
  registerSalonFace,
  registerSalonProfile,
} from '../../face-identity/utils/faceIdentityApi';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import RegisterFormFields from '../../face-identity/components/RegisterFormFields';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useKioskRegisterForm } from '../../face-identity/hooks/useKioskRegisterForm';
import {
  buildRegisterDraft,
  buildRegisterProfilePayload,
  emptyRegisterValues,
  readRegisterPrefillFromNav,
  applyRegisterNavPrefill,
  validateRegisterDraft,
} from '../../face-identity/utils/registerFormHelpers';
import { SALON_HUB_PATH } from '../config/salonApp';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import { buildKioskNavState } from '../../face-identity/utils/kioskNavState';
import { linkPendingCheckinIfNeeded } from '../utils/visitHandoff';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/pages/FaceIdentityRegisterPage.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';

const IconLeaf = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3c.4 3.2 2.2 5.4 4.8 6.2C14.2 10 12.4 12.2 12 15.4 11.6 12.2 9.8 10 7.2 9.2 9.8 8.4 11.6 6.2 12 3z" />
  </svg>
);

const SalonRegisterPage = () => {
  useFaceIdentityScrollShell();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const { registerForm } = useKioskRegisterForm();
  const navigate = useNavigate();
  const location = useLocation();
  const pending = readFacePending();

  const captureUrl =
    location.state?.captureUrl || pending?.captureUrl || readFaceCaptureDataUrl();
  const token = location.state?.token || null;
  const deviceInfo = location.state?.deviceInfo || null;
  const nextPath = location.state?.next || SALON_HUB_PATH;
  const stylistLed = Boolean(location.state?.stylistLed);
  const entryMode = 'salon';
  const pendingCheckinId = location.state?.pendingCheckinId || null;
  const pendingCheckinCode = location.state?.pendingCheckinCode || null;
  const preselectedServices = location.state?.preselectedServices || [];
  const registerMessage =
    location.state?.registerMessage ||
    pending?.registerMessage ||
    (location.state?.registerReason === 'unknown' || pending?.registerReason === 'unknown'
      ? "We didn't recognize you — create a profile to continue."
      : '');

  const [values, setValues] = useState(() => emptyRegisterValues());
  useEffect(() => {
    const prefill = readRegisterPrefillFromNav(location.state, pending);
    if (!prefill.mobile && !prefill.name && !prefill.gender) return;
    setValues((prev) => applyRegisterNavPrefill(prev, registerForm, prefill));
  }, [registerForm, location.state, pending]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const draft = useMemo(() => buildRegisterDraft(values), [values]);

  const validateDraft = () =>
    validateRegisterDraft({
      draft,
      fields: registerForm.fields,
      registerForm,
      hasCapture: stylistLed || Boolean(readFaceCaptureBlob()),
    });

  const handleSubmitRegister = async (e) => {
    e.preventDefault();
    const message = validateDraft();
    if (message) {
      setError(message);
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const profile = buildRegisterProfilePayload(draft, registerForm.fields, registerForm);
      let data;
      if (stylistLed) {
        data = await registerSalonProfile(profile, { newPerson: Boolean(location.state?.newPerson) });
      } else {
        const blob = readFaceCaptureBlob();
        if (!blob) throw new Error('No face capture available — please scan again');
        data = await registerSalonFace(blob, profile, { stylistLed });
      }
      const existingStatuses = new Set(['existing_face', 'existing_mobile', 'found']);
      const isExisting = Boolean(data?.existing) || existingStatuses.has(String(data?.status || ''));
      const user = data?.user || {
        name: profile.name,
        full_name: profile.name,
        mobile: profile.mobile,
      };
      const profilePayload = data?.profile || user?.profile || null;
      const snapshot = {
        faceUserId: user.id || data?.user?.id,
        user,
        profile: profilePayload,
        captureUrl,
        isNewMember: !isExisting,
        mode: 'returning',
        confidence: data?.confidence ?? null,
      };
      persistFaceProfileSnapshot(snapshot);
      persistFacePending(snapshot);
      try {
        await linkPendingCheckinIfNeeded(snapshot.faceUserId, {
          pendingCheckinId,
          name: user.name || user.full_name || profile.name,
          mobile: user.mobile || profile.mobile,
        });
      } catch (_) {
        /* non-blocking */
      }
      if (stylistLed) {
        navigate('/naturals/salon/stylist-checkin', {
          replace: true,
          state: {
            resumeProfile: {
              user,
              mobile: user.mobile || profile.mobile,
            },
            engagementBooking: location.state?.engagementBooking || undefined,
          },
        });
        return;
      }
      navigate('/naturals/salon/welcome', {
        state: {
          ...snapshot,
          token,
          deviceInfo,
          next: nextPath,
          mode: entryMode,
          pendingCheckinId: pendingCheckinId || undefined,
          checkinId: pendingCheckinId || undefined,
          checkinCode: pendingCheckinCode || undefined,
          fromCustomerCode: Boolean(pendingCheckinId),
          preselectedServices,
        },
        replace: true,
      });
    } catch (err) {
      setError(err?.message || 'Registration failed');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fi-register fi-lux-theme fi-scroll-page salon-register-page">
      <div className="fi-register__stage">
        <div className="fi-register__layout">
          <aside className="fi-register__aside">
            <div className="fi-register__brand">
              <FaceIdentityBrandLogo
                showLogo={showLogo}
                orgLogoUrl={orgLogoUrl}
                onLogoError={onLogoError}
                imgClassName="fi-brand-logo__img fi-register__logo"
                fallbackClassName="fi-register__logo-text"
              />
            </div>

            {captureUrl && (
              <div className="fi-register__portrait-wrap">
                <div className="fi-register__portrait">
                  <img src={captureUrl} alt="Captured face" />
                </div>
                <span className="fi-register__badge" aria-hidden="true">
                  <IconLeaf />
                </span>
              </div>
            )}

            {registerMessage ? (
              <p className="fi-register__aside-lede">{registerMessage}</p>
            ) : null}
          </aside>

          <form className="fi-register__panel" onSubmit={handleSubmitRegister}>
            <header className="fi-register__panel-head">
              <div className="fi-register__panel-head-row">
                <FaceIdentityBrandLogo
                  showLogo={showLogo}
                  orgLogoUrl={orgLogoUrl}
                  onLogoError={onLogoError}
                  imgClassName="fi-brand-logo__img fi-brand-logo__img--panel"
                  fallbackClassName="fi-brand-logo__fallback fi-brand-logo__fallback--panel"
                />
                <FaceIdentityFlowNav
                  onBack={() =>
                    navigate('/naturals/salon/scan', {
                      replace: true,
                      state: buildKioskNavState(location.state, {
                        token,
                        deviceInfo,
                        next: nextPath,
                        mode: 'salon',
                      }),
                    })
                  }
                  onHome={() =>
                    navigate(resolveSalonHomePath(), { replace: true })
                  }
                  homePath={resolveSalonHomePath()}
                />
              </div>
              <h1 className="fi-register__panel-title">{registerForm.title}</h1>
              {registerForm.subtitle ? (
                <p className="fi-register__panel-meta">{registerForm.subtitle}</p>
              ) : null}
            </header>

            <section className="fi-register__section" aria-labelledby="fi-register-details">
              <div className="fi-register__section-head">
                <h2 id="fi-register-details">Your details</h2>
              </div>
              <RegisterFormFields
                registerForm={registerForm}
                values={values}
                setValues={setValues}
              />
            </section>

            {error && <p className="fi-register__error">{error}</p>}

            <div className="fi-register__actions">
              <button type="submit" className="fi-register__btn fi-register__btn--primary" disabled={isSubmitting}>
                {isSubmitting ? 'Registering…' : 'Register'}
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SalonRegisterPage;
